/* ============================================================================
   THEME.JS — Cart drawer, filter sidebar, quantity stepper, accordion, tabs
   ============================================================================ */

/* ── CART ─────────────────────────────────────────────────────────────────── */

const Cart = (() => {
  const drawer = document.getElementById('cart-drawer');
  const body = document.getElementById('cart-drawer-body');
  const subtotal = document.getElementById('cart-subtotal');

  function open() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    refresh();
  }

  function close() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  async function refresh() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      render(cart);
    } catch (e) {
      console.error('Cart fetch failed', e);
    }
  }

  function render(cart) {
    if (!body || !subtotal) return;

    if (cart.items.length === 0) {
      body.innerHTML = `<p class="cart-drawer__empty">Your cart's empty. Go find the pull.</p>`;
      subtotal.textContent = formatMoney(0);
      updateCount(0);
      return;
    }

    body.innerHTML = cart.items.map(item => `
      <div class="cart-item">
        <div class="cart-item__image">
          ${item.featured_image ? `<img src="${item.featured_image.url}" alt="${item.title}" loading="lazy">` : ''}
        </div>
        <div class="cart-item__details">
          <p class="cart-item__title">${item.product_title}</p>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant ct-small">${item.variant_title}</p>` : ''}
          <div class="cart-item__footer">
            <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>
            <button class="cart-item__remove" data-remove-key="${item.key}" aria-label="Remove ${item.title}">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>
      </div>
    `).join('');

    subtotal.textContent = formatMoney(cart.total_price);

    updateCount(cart.item_count);

    body.querySelectorAll('[data-remove-key]').forEach(btn => {
      btn.addEventListener('click', () => removeItem(btn.dataset.removeKey));
    });
  }

  function updateCount(count) {
    const el = document.getElementById('cart-count');
    if (!el) return;
    el.textContent = count;
    el.setAttribute('data-count', count);
  }

  async function addItem(variantId, quantity = 1, properties = {}) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity, properties }),
      });
      if (!res.ok) throw new Error('Add to cart failed');
      open();
    } catch (e) {
      console.error(e);
    }
  }

  async function removeItem(key) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      refresh();
    } catch (e) {
      console.error(e);
    }
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2) + ' AUD';
  }

  function isOpen() {
    return drawer ? drawer.getAttribute('aria-hidden') === 'false' : false;
  }

  return { open, close, addItem, refresh, updateCount, isOpen };
})();

/* ── INIT ─────────────────────────────────────────────────────────────────── */

document.addEventListener('DOMContentLoaded', () => {

  /* Cart triggers */
  document.querySelectorAll('[data-cart-open]').forEach(el => {
    el.addEventListener('click', (e) => { e.preventDefault(); Cart.open(); });
  });
  document.querySelectorAll('[data-cart-close]').forEach(el => {
    el.addEventListener('click', Cart.close);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') Cart.close();
  });

  /* Add to cart forms */
  document.querySelectorAll('[data-add-to-cart]').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const variantInput = form.querySelector('[name="id"]');
      const qtyInput = form.querySelector('[name="quantity"]');
      if (!variantInput) return;
      const variantId = variantInput.value;
      const quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      const btn = form.querySelector('[type="submit"]');
      const label = btn ? (btn.querySelector('[data-add-label]') || btn) : null;
      const originalText = label ? label.textContent : '';
      if (btn) {
        btn.disabled = true;
        label.textContent = 'Adding…';
      }
      await Cart.addItem(variantId, quantity);
      if (btn) {
        btn.disabled = false;
        label.textContent = originalText.trim() || 'Add to cart';
      }
    });
  });

  /* Quick-add buttons (product card) */
  document.querySelectorAll('[data-quick-add]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const variantId = btn.dataset.quickAdd;
      if (!variantId) return;
      await Cart.addItem(variantId, 1);
    });
  });

  /* Quantity steppers (cart-page steppers are handled by the cart-page
     handler below, which persists changes to the cart) */
  document.querySelectorAll('.qty-stepper:not([data-cart-line-stepper])').forEach(stepper => {
    const input = stepper.querySelector('.qty-stepper__value');
    stepper.querySelector('[data-qty-dec]')?.addEventListener('click', () => {
      const v = parseInt(input.value || 1);
      if (v > 1) input.value = v - 1;
    });
    stepper.querySelector('[data-qty-inc]')?.addEventListener('click', () => {
      const v = parseInt(input.value || 1);
      const max = parseInt(input.max) || Infinity;
      if (v < max) input.value = v + 1;
    });
  });

  /* Cart page — server-rendered line items, re-rendered via the
     Section Rendering API after each /cart/change.js call */
  if (document.querySelector('[data-cart-page]')) {
    let cartPageBusy = false;

    async function changeLine(key, quantity) {
      if (cartPageBusy) return;
      const root = document.querySelector('[data-cart-page]');
      if (!root) return;
      cartPageBusy = true;

      const sectionId = root.dataset.sectionId;
      const sectionEl = document.getElementById('shopify-section-' + sectionId);
      sectionEl?.setAttribute('aria-busy', 'true');

      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: sectionId }),
        });
        if (!res.ok) throw new Error('Cart update failed');
        const cart = await res.json();

        const rendered = cart.sections && cart.sections[sectionId];
        if (sectionEl && rendered) {
          const doc = new DOMParser().parseFromString(rendered, 'text/html');
          const fresh = doc.getElementById('shopify-section-' + sectionId);
          if (fresh) sectionEl.innerHTML = fresh.innerHTML;
        }

        Cart.updateCount(cart.item_count);
        if (Cart.isOpen()) Cart.refresh();
      } catch (e) {
        console.error(e);
        const status = document.querySelector('[data-cart-status]');
        if (status) status.textContent = root.dataset.errorMessage || '';
      } finally {
        sectionEl?.removeAttribute('aria-busy');
        cartPageBusy = false;
      }
    }

    /* Delegate on document so handlers survive section re-renders */
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-cart-page]')) return;

      const remove = e.target.closest('[data-remove-key]');
      if (remove) {
        e.preventDefault();
        changeLine(remove.dataset.removeKey, 0);
        return;
      }

      const dec = e.target.closest('[data-qty-dec]');
      const inc = e.target.closest('[data-qty-inc]');
      const row = e.target.closest('[data-cart-row]');
      if (!row || (!dec && !inc)) return;

      const input = row.querySelector('input[name="updates[]"]');
      if (!input) return;
      const current = parseInt(input.value, 10) || 0;
      const max = parseInt(input.max, 10) || Infinity;
      const next = Math.max(0, Math.min(dec ? current - 1 : current + 1, max));
      if (next === current) return;
      changeLine(row.dataset.key, next);
    });

    document.addEventListener('change', (e) => {
      if (!e.target.matches('[data-cart-page] input[name="updates[]"]')) return;
      const row = e.target.closest('[data-cart-row]');
      if (!row) return;
      changeLine(row.dataset.key, Math.max(0, parseInt(e.target.value, 10) || 0));
    });
  }

  /* Accordions */
  document.querySelectorAll('.accordion__trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const item = trigger.closest('.accordion__item');
      const isOpen = item.classList.contains('accordion__item--open');
      const group = item.closest('[data-accordion-group]');
      if (group) {
        /* Close every open item in the group (accordion behaviour) */
        group.querySelectorAll('.accordion__item--open').forEach(open => {
          open.classList.remove('accordion__item--open');
          open.querySelector('.accordion__trigger')?.setAttribute('aria-expanded', 'false');
        });
      } else if (isOpen) {
        /* Standalone item — allow it to toggle closed */
        item.classList.remove('accordion__item--open');
        trigger.setAttribute('aria-expanded', 'false');
      }
      if (!isOpen) {
        item.classList.add('accordion__item--open');
        trigger.setAttribute('aria-expanded', 'true');
      }
    });
  });

  /* Tab navs */
  document.querySelectorAll('.tab-nav').forEach(nav => {
    nav.querySelectorAll('.tab-nav__btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const target = btn.dataset.tabTarget;
        const container = btn.closest('[data-tabs]') || document;
        nav.querySelectorAll('.tab-nav__btn').forEach(b => b.classList.remove('tab-nav__btn--active'));
        btn.classList.add('tab-nav__btn--active');
        container.querySelectorAll('[data-tab-panel]').forEach(panel => {
          panel.hidden = panel.dataset.tabPanel !== target;
        });
      });
    });
  });

  /* Variant selection is handled per-section in product-main.liquid,
     which resolves the matching variant ID from the full variant map. */

  /* Mobile nav toggle */
  const navToggle = document.querySelector('[data-nav-toggle]');
  const mobileNav = document.querySelector('[data-mobile-nav]');
  if (navToggle && mobileNav) {
    navToggle.addEventListener('click', () => {
      const open = mobileNav.getAttribute('aria-hidden') === 'false';
      mobileNav.setAttribute('aria-hidden', open ? 'true' : 'false');
      navToggle.setAttribute('aria-expanded', open ? 'false' : 'true');
    });
  }

  /* Filter sidebar (collection page) */
  const filterToggle = document.querySelector('[data-filter-toggle]');
  const filterSidebar = document.querySelector('[data-filter-sidebar]');
  if (filterToggle && filterSidebar) {
    const FILTER_OPEN_KEY = 'cta:filter-sidebar-open';

    const setSidebarOpen = (open) => {
      filterSidebar.setAttribute('aria-hidden', open ? 'false' : 'true');
      filterToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    };

    filterToggle.addEventListener('click', () => {
      const open = filterSidebar.getAttribute('aria-hidden') === 'false';
      setSidebarOpen(!open);
    });

    // A filter change submits the form and reloads the page, which would
    // otherwise reset the sidebar to its collapsed default on mobile.
    // Remember the open state across that reload and restore it on load.
    const filterForm = document.getElementById('filter-form');
    if (filterForm) {
      filterForm.addEventListener('submit', () => {
        try { sessionStorage.setItem(FILTER_OPEN_KEY, '1'); } catch (e) {}
      });
    }

    try {
      if (sessionStorage.getItem(FILTER_OPEN_KEY) === '1') {
        sessionStorage.removeItem(FILTER_OPEN_KEY);
        setSidebarOpen(true);
      }
    } catch (e) {}
  }

  /* Collapsible filter groups */
  {
    const COLLAPSED_KEY = 'cta:filters-collapsed';

    const readCollapsed = () => {
      try {
        const stored = JSON.parse(sessionStorage.getItem(COLLAPSED_KEY) || '[]');
        return Array.isArray(stored) ? stored : [];
      } catch (e) {
        return [];
      }
    };

    /* Delegate on document so handlers survive section re-renders */
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-filter-group-toggle]');
      if (!toggle) return;

      const group = toggle.closest('[data-filter-group]');
      if (!group) return;

      const collapse = !group.classList.contains('filter-group--collapsed');
      group.classList.toggle('filter-group--collapsed', collapse);
      toggle.setAttribute('aria-expanded', collapse ? 'false' : 'true');

      // Groups are independent, so store the full collapsed set. Filtering
      // reloads the page, which would otherwise reset every group to open.
      const key = group.dataset.filterKey;
      if (!key) return;
      const collapsed = readCollapsed().filter(k => k !== key);
      if (collapse) collapsed.push(key);
      try {
        sessionStorage.setItem(COLLAPSED_KEY, JSON.stringify(collapsed));
      } catch (err) {}
    });
  }

  /* Product gallery thumbnails */
  document.querySelectorAll('[data-thumb]').forEach(thumb => {
    thumb.addEventListener('click', () => {
      const gallery = thumb.closest('[data-gallery]');
      if (!gallery) return;
      const main = gallery.querySelector('[data-gallery-main]');
      if (main) {
        const newSrc = thumb.dataset.thumb;
        const img = main.querySelector('img');
        if (img) img.src = newSrc;
        gallery.querySelectorAll('[data-thumb]').forEach(t => t.classList.remove('gallery__thumb--active'));
        thumb.classList.add('gallery__thumb--active');
      }
    });
  });

  /* Announcement bar dismiss */
  const announceDismiss = document.querySelector('[data-announce-dismiss]');
  if (announceDismiss) {
    announceDismiss.addEventListener('click', () => {
      const bar = announceDismiss.closest('.announcement-bar');
      if (bar) bar.style.display = 'none';
    });
  }

  /* Grading submission form */
  const gradingForm = document.querySelector('[data-grading-submit-form]');
  if (gradingForm) {
    const rowsContainer = gradingForm.closest('.grading-submit')?.querySelector('[data-grading-card-rows]');
    const rowTemplate = gradingForm.closest('.grading-submit')?.querySelector('[data-grading-row-template]');
    const messages = gradingForm.closest('.grading-submit')?.querySelector('[data-grading-form-messages]');
    const addRowBtn = gradingForm.querySelector('[data-grading-add-row]');

    function addCardRow() {
      if (!rowsContainer || !rowTemplate) return;
      const node = rowTemplate.content.cloneNode(true);
      rowsContainer.appendChild(node);
    }

    function bindRemoveButtons() {
      rowsContainer?.querySelectorAll('[data-grading-remove-row]').forEach((btn) => {
        if (btn.dataset.bound) return;
        btn.dataset.bound = 'true';
        btn.addEventListener('click', () => {
          const row = btn.closest('[data-grading-row]');
          const allRows = rowsContainer.querySelectorAll('[data-grading-row]');
          if (row && allRows.length > 1) row.remove();
        });
      });
    }

    /* Watch for newly added rows so their remove buttons get bound too */
    if (rowsContainer && window.MutationObserver) {
      new MutationObserver(bindRemoveButtons).observe(rowsContainer, { childList: true });
    }

    addRowBtn?.addEventListener('click', addCardRow);
    addCardRow(); /* start with one row */

    function showMessage(text, type) {
      if (!messages) return;
      const role = type === 'error' ? 'alert' : 'status';
      messages.innerHTML = `<p class="grading-submit-form-message grading-submit-form-message--${type}" role="${role}">${text}</p>`;
    }

    function serializeForm() {
      const formData = new FormData(gradingForm);
      const cards = [];
      rowsContainer?.querySelectorAll('[data-grading-row]').forEach((row) => {
        cards.push({
          game: row.querySelector('[name="cards[][game]"]')?.value || '',
          card_name: row.querySelector('[name="cards[][card_name]"]')?.value || '',
          set_name: row.querySelector('[name="cards[][set_name]"]')?.value || '',
          declared_value: row.querySelector('[name="cards[][declared_value]"]')?.value || '',
        });
      });
      return {
        company: formData.get('company'),
        tier: formData.get('tier'),
        notes: formData.get('notes'),
        contact_email: formData.get('contact_email'),
        contact_phone: formData.get('contact_phone'),
        cards,
      };
    }

    gradingForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = gradingForm.querySelector('[data-grading-submit-btn]');
      const label = gradingForm.querySelector('[data-grading-submit-label]');
      const originalText = label ? label.textContent : '';
      if (btn) {
        btn.disabled = true;
        if (label) label.textContent = 'Sending…';
      }

      try {
        /* Served by the grading app's app-proxy endpoint — see the
           companion grading app repo, not this theme. */
        const res = await fetch('/apps/grading/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(serializeForm()),
        });
        if (!res.ok) throw new Error('Submission failed');
        showMessage(
          gradingForm.dataset.successMessage || "Submission received — we'll review it and be in touch before any payment is taken.",
          'success'
        );
        gradingForm.reset();
      } catch (err) {
        console.error(err);
        showMessage(
          gradingForm.dataset.errorMessage || 'Something went wrong sending your submission. Please try again or contact us directly.',
          'error'
        );
      } finally {
        if (btn) {
          btn.disabled = false;
          if (label) label.textContent = originalText.trim() || 'Submit for review';
        }
      }
    });
  }

});
